import { H5, Icon, Intent, MaybeElement } from "@blueprintjs/core";
import { IconName } from "@blueprintjs/icons";
import classNames from "classnames";
import css from "./Banner.module.scss";
import { Surface } from "./Surface";

/** Where the action sits; `top` and `center` differ only on a two-line banner. */
export type BannerActionPlacement = "top" | "center" | "bottom";

interface BannerProps {
    readonly title: React.ReactNode;
    readonly icon?: IconName | MaybeElement;
    readonly description: React.ReactNode;
    readonly action?: React.ReactNode;
    readonly actionPlacement?: BannerActionPlacement;
    readonly intent?: Intent;
    readonly className?: string;
}

export const Banner: React.FC<BannerProps> = ({
    title,
    icon,
    description,
    action,
    actionPlacement = "center",
    intent,
    className
}) => {
    const hasIntent = intent !== undefined && intent !== Intent.NONE;
    const inFooter = actionPlacement === "bottom";
    return (
        <Surface
            className={classNames(
                css["Banner"],
                { [css["Banner--multiline"]]: Boolean(description) },
                className
            )}
            padding="none"
            radius="md"
            intent={hasIntent ? intent : undefined}
            tone={hasIntent ? "none" : "tint"}
            bordered={!hasIntent}
        >
            <div className={css["Banner-main"]}>
                {icon && (
                    <span className={css["Banner-chip"]}>
                        {typeof icon === "string" ? (
                            <Icon icon={icon} size={20} />
                        ) : (
                            icon
                        )}
                    </span>
                )}
                <div className={css["Banner-text"]}>
                    <H5 className={css["Banner-title"]}>{title}</H5>
                    {description && (
                        <span className={css["Banner-description"]}>
                            {description}
                        </span>
                    )}
                </div>
                {action && !inFooter && (
                    <div
                        className={classNames(
                            css["Banner-action"],
                            css[`Banner-action--${actionPlacement}`]
                        )}
                    >
                        {action}
                    </div>
                )}
            </div>
            {action && inFooter && (
                <div className={css["Banner-footer"]}>{action}</div>
            )}
        </Surface>
    );
};
